import React, { useRef, ReactNode, useMemo, forwardRef, Ref, ButtonHTMLAttributes, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { useEventListener } from 'primereact/hooks';
import composeRefs from '@seznam/compose-react-refs';
import { findFocusableElements } from 'utils/findFocusableElements';
import { Overlay, OverlayElement, OverlayExtendProps } from '../Overlay';
import { Box, BoxSection, BoxSectionHeader } from '../Box';
import { LinkProps } from '../Link';
import { Icon, IconStyle } from '../Icon';
import './Menu.scss';
import { Tooltip } from '../Tooltip/Tooltip';

type MenuItemBase = {
	key: string;
	icon?: string;
	iconStyle?: IconStyle;
	label: string;
	disabled?: boolean;
	tooltip?: string;
	itemClassName?: string;
};

export type MenuItemButton = { type: 'button' } & ButtonHTMLAttributes<HTMLButtonElement> & MenuItemBase;
export type MenuItemLink = { type: 'link' } & LinkProps & MenuItemBase;

export type MenuItem = MenuItemButton | MenuItemLink;

export type MenuVariant = 'standard' | 'additional-pads' | 'bigger';
type BoxVariant = 'white' | 'white-bordered' | 'blue' | 'blue-rose-bordered' | 'clean';

export type MenuProps = OverlayExtendProps & {
	header?: BoxSectionHeader;
	items?: MenuItem[];
	variant?: MenuVariant;
	boxVariant?: BoxVariant;
	boxClassName?: string;
};

export const Menu = forwardRef(
	(
		{ header, items, variant = 'standard', boxVariant = 'white-bordered', boxClassName, ...restProps }: MenuProps,
		ref: Ref<OverlayElement>
	) => {
		const overlayRef = useRef<OverlayElement>(null);

		const renderContent = ({ icon, iconStyle, label }: MenuItem) => (
			<>
				{icon && <Icon name={icon} iconStyle={iconStyle} className="mr-1" />}
				<span>{label}</span>
			</>
		);

		const finalItems = useMemo(
			() =>
				(items || [])
					.map((item) => {
						const { key, title, disabled, tooltip, itemClassName } = item;
						const id = `item-${key}`;
						if (item.type === 'link') {
							const { to, onClick } = item;
							const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
								if (disabled) {
									event.preventDefault();
								} else {
									if (onClick) {
										onClick(event);
									}
									if (overlayRef?.current) {
										overlayRef?.current.close();
									}
								}
							};
							return (
								<li key={key} className={classNames('box-section-list-item', itemClassName)}>
									{tooltip && <Tooltip content={tooltip} target={`#${id}`} position="left" />}
									<Link
										id={id}
										to={to}
										className={classNames(
											'menu-item',
											{ 'menu-item-disabled': disabled },
											item.className
										)}
										onClick={handleClick}
										title={title}
									>
										{renderContent(item)}
									</Link>
								</li>
							);
						}
						if (item.type === 'button') {
							const { onClick } = item;
							const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
								if (disabled) {
									event.preventDefault();
								} else {
									if (onClick) {
										onClick(event);
									}
									if (overlayRef?.current) {
										overlayRef?.current.close();
									}
								}
							};
							return (
								<li key={key} className={classNames('box-section-list-item', itemClassName)}>
									{tooltip && <Tooltip content={tooltip} target={`#${id}`} position="left" />}
									<button
										id={id}
										type="button"
										onClick={handleClick}
										className={classNames(
											'menu-item',
											{ 'menu-item-disabled': disabled },
											item.className
										)}
										title={title}
									>
										{renderContent(item)}
									</button>
								</li>
							);
						}
						return null;
					})
					.filter(Boolean) as ReactNode[],
			[items]
		);

		const handleDocumentKeyDown = (event: KeyboardEvent) => {
			// close on esc
			if (event.key === 'Escape') {
				event.preventDefault();
				overlayRef?.current?.close();
			}

			// prevent tab outside menu
			if (event.key === 'Tab') {
				const focusable = findFocusableElements(boxRef.current);
				const lastFocusable = focusable[focusable.length - 1];

				// tab next
				if (!event.shiftKey && event.target === lastFocusable) {
					event.preventDefault();
					focusable[0].focus();
				}

				// tab prev
				if (event.shiftKey && event.target === focusable[0]) {
					event.preventDefault();
					lastFocusable.focus();
				}
			}
		};

		const [bindDocumentKeyDownListener, unbindDocumentKeyDownListener] = useEventListener({
			type: 'keydown',
			listener: handleDocumentKeyDown,
		});

		const boxRef = useRef<HTMLDivElement>(null);
		const handleOpen = () => {
			if (boxRef?.current) {
				const focusable = findFocusableElements(boxRef.current);
				if (focusable.length > 0) {
					focusable[0].focus();
				}
			}
			bindDocumentKeyDownListener();
		};
		const handleClose = (referenceElement: HTMLElement) => {
			referenceElement.focus();
			unbindDocumentKeyDownListener();
		};

		return (
			<Overlay ref={composeRefs(ref, overlayRef)} onOpen={handleOpen} onClose={handleClose} {...restProps}>
				<Box
					ref={boxRef}
					variant={boxVariant}
					className={classNames('menu', `menu-${variant}`)}
					style={{ minWidth: 100 }}
				>
					{!items?.length ? (
						<BoxSection header={header} className={boxClassName}>
							None
						</BoxSection>
					) : (
						<BoxSection noPad header={header}>
							<ul className={classNames('box-section-list', `box-section-list-${variant}`, boxClassName)}>
								{finalItems}
							</ul>
						</BoxSection>
					)}
				</Box>
			</Overlay>
		);
	}
);
